import React, { useState } from 'react';
import {
    Box, Typography, TextField, Button, Stepper, Step, StepLabel,
    Card, Checkbox, FormControlLabel, Alert, CircularProgress,
    List, ListItem, ListItemIcon, ListItemText, Chip, Divider,
    InputAdornment
} from '@mui/material';
import {
    DeleteForever as DeleteForeverIcon,
    Email as EmailIcon,
    Warning as WarningIcon,
    CheckCircle as CheckCircleIcon,
    Person as PersonIcon,
    SportsScore as SportsScoreIcon,
    CreditCard as CreditCardIcon,
    Photo as PhotoIcon,
    Settings as SettingsIcon,
    ArrowBack as ArrowBackIcon,
    ArrowForward as ArrowForwardIcon,
} from '@mui/icons-material';
import axios from 'axios';
import config from '../config/config.json';

const host = config.production.host;

// ─── Brand palette ───
const C = {
    bg: '#181818',
    bgCard: '#222222',
    border: 'rgba(255,255,255,0.08)',
    yellow: '#F5C000',
    yellowDim: 'rgba(245,192,0,0.15)',
    yellowBrd: 'rgba(245,192,0,0.4)',
    white: '#FFFFFF',
    muted: 'rgba(255,255,255,0.55)',
    dim: 'rgba(255,255,255,0.12)',
    red: '#e53935',
    redDim: 'rgba(229,57,53,0.15)',
    redBrd: 'rgba(229,57,53,0.4)',
    green: '#43a047',
    greenDim: 'rgba(67,160,71,0.15)',
    greenBrd: 'rgba(67,160,71,0.4)',
};

const fieldSx = (focusColor = C.yellow) => ({
    mb: 3,
    '& .MuiOutlinedInput-root': {
        color: C.white,
        '& fieldset': { borderColor: C.border },
        '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
        '&.Mui-focused fieldset': { borderColor: focusColor },
    },
    '& .MuiInputBase-input, & .MuiOutlinedInput-input, & textarea': {
        color: `${C.white} !important`,
        WebkitTextFillColor: `${C.white} !important`,
    },
    '& .MuiInputLabel-root': { color: C.muted },
    '& .MuiInputLabel-root.Mui-focused': { color: focusColor },
    '& .MuiOutlinedInput-input::placeholder, & textarea::placeholder': { color: 'rgba(255,255,255,0.25)' },
});

const STEPS = ['Inserisci Email', 'Conferma Cancellazione', 'Richiesta Inviata'];

const DATA_TO_DELETE = [
    { icon: <PersonIcon />, label: 'Dati del profilo', desc: 'Nome, cognome, foto profilo, informazioni personali' },
    { icon: <SportsScoreIcon />, label: 'Dati sportivi', desc: 'Squadre, statistiche, partite, ruoli' },
    { icon: <CreditCardIcon />, label: 'Abbonamenti', desc: 'Storico abbonamenti e transazioni' },
    { icon: <PhotoIcon />, label: 'Contenuti pubblicati', desc: 'Post, immagini e contenuti condivisi' },
    { icon: <SettingsIcon />, label: 'Impostazioni', desc: 'Preferenze, colori, configurazioni personali' },
];

const AccountDeletionPage = () => {
    const [activeStep, setActiveStep] = useState(0);
    const [email, setEmail] = useState('');
    const [motivo, setMotivo] = useState('');
    const [confirmed, setConfirmed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    const handleSubmitEmail = async (e) => {
        e.preventDefault();
        if (!email || !email.includes('@')) {
            setError('Inserisci un indirizzo email valido.');
            return;
        }
        setError('');
        setLoading(true);

        try {
            // Sfruttiamo l'endpoint di creazione con il flag validateOnly
            await axios.post(`${host}/tbl-delete-requests`, {
                email: email.trim().toLowerCase(),
                validateOnly: true
            });
            
            setActiveStep(1);
        } catch (err) {
            console.error('Errore verifica email:', err);
            if (err.response && err.response.data && err.response.data.message) {
                setError(err.response.data.message);
            } else {
                setError("Si è verificato un errore durante la verifica. Riprova più tardi.");
            }
        } finally {
            setLoading(false);
        }
    };

    const handleConfirm = async () => {
        if (!confirmed) {
            setError('Devi confermare di aver letto le informazioni.');
            return;
        }
        setError('');
        setLoading(true);
        try {
            await axios.post(`${host}/tbl-delete-requests`, {
                email: email.trim().toLowerCase(),
                motivo: motivo.trim() || null,
                status: 'pending',
                ipAddress: null
            });
            setActiveStep(2);
        } catch (err) {
            console.error('Errore invio richiesta cancellazione:', err);
            if (err.response && err.response.data && err.response.data.message) {
                setError(err.response.data.message);
                // Go back to the first step so the user can fix the email
                setConfirmed(false);
                setActiveStep(0);
            } else {
                setError("Si è verificato un errore durante l'invio della richiesta. Riprova più tardi o contatta il supporto.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box sx={{
            minHeight: '100vh',
            bgcolor: C.bg,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pt: { xs: 5, md: 9 },
            pb: 10,
            px: 2,
        }}>
            {/* ── Logo / Brand line ── */}
            <Box sx={{ textAlign: 'center', mb: 5 }}>
                <Typography
                    variant="h6"
                    sx={{
                        color: C.yellow,
                        fontWeight: 800,
                        letterSpacing: 3,
                        textTransform: 'uppercase',
                        mb: 3,
                        fontSize: '0.85rem',
                    }}
                >
                    BEAthlete
                </Typography>

                {/* Icon */}
                <Box sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 68,
                    height: 68,
                    borderRadius: '50%',
                    bgcolor: C.yellowDim,
                    border: `2px solid ${C.yellowBrd}`,
                    mb: 2.5,
                }}>
                    <DeleteForeverIcon sx={{ fontSize: 34, color: C.yellow }} />
                </Box>

                <Typography variant="h4" sx={{
                    color: C.white,
                    fontWeight: 800,
                    mb: 0.5,
                    // yellow underline like the site
                    display: 'inline-block',
                    borderBottom: `3px solid ${C.yellow}`,
                    pb: 0.5,
                }}>
                    Cancellazione Account
                </Typography>
                <Typography variant="body1" sx={{ color: C.muted, maxWidth: 460, mx: 'auto', mt: 2 }}>
                    Richiedi la cancellazione permanente del tuo account e di tutti i dati associati.
                </Typography>
            </Box>

            {/* ── Stepper ── */}
            <Box sx={{ width: '100%', maxWidth: 580, mb: 4 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {STEPS.map((label) => (
                        <Step key={label}>
                            <StepLabel sx={{
                                '& .MuiStepLabel-label': { color: C.muted },
                                '& .MuiStepLabel-label.Mui-active': { color: C.white, fontWeight: 700 },
                                '& .MuiStepLabel-label.Mui-completed': { color: C.muted },
                                '& .MuiStepIcon-root': { color: C.dim },
                                '& .MuiStepIcon-root.Mui-active': { color: C.yellow },
                                '& .MuiStepIcon-root.Mui-completed': { color: C.yellow },
                                '& .MuiStepIcon-text': { fill: C.bg },
                            }}>
                                {label}
                            </StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            {/* ── Card ── */}
            <Card sx={{
                width: '100%',
                maxWidth: 580,
                borderRadius: 3,
                bgcolor: C.bgCard,
                border: `1px solid ${C.border}`,
                boxShadow: '0 16px 48px rgba(0,0,0,0.5)',
                overflow: 'hidden',
            }}>

                {/* STEP 0 — Email */}
                {activeStep === 0 && (
                    <Box component="form" onSubmit={handleSubmitEmail} autoComplete="off" sx={{ p: { xs: 3, md: 4 } }}>
                        <Typography variant="h6" sx={{ color: C.white, fontWeight: 700, mb: 0.5 }}>
                            Inserisci il tuo indirizzo email
                        </Typography>
                        <Typography variant="body2" sx={{ color: C.muted, mb: 3 }}>
                            Inserisci l'email con cui sei registrato. La tua richiesta verrà elaborata dal nostro team entro 30 giorni.
                        </Typography>

                        <TextField
                            fullWidth
                            label="Indirizzo Email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            autoComplete="new-password" // Prevents Chrome autofill completely
                            inputProps={{
                                autoComplete: 'new-password',
                                spellCheck: 'false'
                            }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <EmailIcon sx={{ color: C.muted }} />
                                    </InputAdornment>
                                )
                            }}
                            sx={fieldSx()}
                        />

                        <TextField
                            fullWidth
                            label="Motivo (opzionale)"
                            multiline
                            rows={3}
                            value={motivo}
                            onChange={(e) => setMotivo(e.target.value)}
                            placeholder="Raccontaci il motivo della cancellazione per aiutarci a migliorare..."
                            autoComplete="off"
                            inputProps={{
                                autoComplete: 'off'
                            }}
                            sx={fieldSx()}
                        />

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        <Button
                            type="submit"
                            fullWidth
                            variant="contained"
                            disabled={loading}
                            endIcon={loading ? <CircularProgress size={18} sx={{ color: '#111' }} /> : <ArrowForwardIcon />}
                            sx={{
                                bgcolor: C.yellow,
                                color: '#111',
                                '&:hover': { bgcolor: '#e0ae00' },
                                '&.Mui-disabled': { bgcolor: C.yellowDim, color: 'rgba(255,255,255,0.4)' },
                                py: 1.5,
                                fontWeight: 700,
                                fontSize: '1rem',
                                borderRadius: 2,
                                letterSpacing: 0.5,
                            }}
                        >
                            {loading ? 'Verifica in corso...' : 'Continua'}
                        </Button>
                    </Box>
                )}

                {/* STEP 1 — Conferma */}
                {activeStep === 1 && (
                    <Box sx={{ p: { xs: 3, md: 4 } }}>
                        {/* Warning banner */}
                        <Box sx={{
                            bgcolor: C.yellowDim,
                            border: `1px solid ${C.yellowBrd}`,
                            borderRadius: 2,
                            p: 2,
                            mb: 3,
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'flex-start',
                        }}>
                            <WarningIcon sx={{ color: C.yellow, mt: 0.25, flexShrink: 0 }} />
                            <Box>
                                <Typography variant="body2" sx={{ color: C.yellow, fontWeight: 700, mb: 0.25 }}>
                                    Attenzione: questa operazione è irreversibile.
                                </Typography>
                                <Typography variant="body2" sx={{ color: C.muted }}>
                                    Una volta elaborata, la richiesta non potrà essere annullata.
                                </Typography>
                            </Box>
                        </Box>

                        {/* <Typography variant="body2" sx={{ color: C.muted, mb: 0.75 }}>
                            Richiesta per l'account:
                        </Typography>
                        <Chip
                            icon={<EmailIcon sx={{ color: `${C.yellow} !important` }} />}
                            label={email}
                            sx={{ mb: 3, bgcolor: C.yellowDim, color: C.white, border: `1px solid ${C.yellowBrd}`, fontWeight: 600 }}
                        />

                        <Typography variant="body2" sx={{ color: C.white, fontWeight: 700, mb: 1.5 }}>
                            I seguenti dati verranno cancellati permanentemente:
                        </Typography>

                        <List dense disablePadding sx={{ mb: 3 }}>
                            {DATA_TO_DELETE.map((item, idx) => (
                                <React.Fragment key={idx}>
                                    <ListItem disableGutters sx={{ py: 1 }}>
                                        <ListItemIcon sx={{ minWidth: 36, color: C.yellow }}>
                                            {item.icon}
                                        </ListItemIcon>
                                        <ListItemText
                                            primary={<Typography variant="body2" sx={{ color: C.white, fontWeight: 600 }}>{item.label}</Typography>}
                                            secondary={<Typography variant="caption" sx={{ color: C.muted }}>{item.desc}</Typography>}
                                        />
                                    </ListItem>
                                    {idx < DATA_TO_DELETE.length - 1 && <Divider sx={{ borderColor: C.border }} />}
                                </React.Fragment>
                            ))}
                        </List> */}

                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={confirmed}
                                    onChange={(e) => setConfirmed(e.target.checked)}
                                    sx={{ color: C.muted, '&.Mui-checked': { color: C.yellow } }}
                                />
                            }
                            label={
                                <Typography variant="body2" sx={{ color: C.muted }}>
                                    Ho letto le informazioni di{' '}
                                    <a
                                        href="https://besocialsport.it/privacy-policy"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        style={{ color: C.yellow, textDecoration: 'underline' }}
                                    >
                                        https://besocialsport.it/privacy-policy
                                    </a>
                                    {' '}e acconsento alla cancellazione permanente del mio account e di tutti i dati associati.
                                </Typography>
                            }
                            sx={{ mb: 3, alignItems: 'flex-start' }}
                        />

                        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                        <Box sx={{ display: 'flex', gap: 2 }}>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => { setActiveStep(0); setConfirmed(false); setError(''); }}
                                sx={{
                                    flex: 1,
                                    borderColor: C.border,
                                    color: C.muted,
                                    '&:hover': { borderColor: 'rgba(255,255,255,0.3)', bgcolor: 'rgba(255,255,255,0.04)' },
                                    py: 1.5,
                                    borderRadius: 2,
                                }}
                            >
                                Indietro
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={loading ? <CircularProgress size={18} sx={{ color: '#111' }} /> : <DeleteForeverIcon />}
                                onClick={handleConfirm}
                                disabled={loading}
                                sx={{
                                    flex: 2,
                                    bgcolor: C.red,
                                    color: C.white,
                                    '&:hover': { bgcolor: '#c62828' },
                                    '&.Mui-disabled': { bgcolor: C.redDim, color: 'rgba(255,255,255,0.4)' },
                                    py: 1.5,
                                    fontWeight: 700,
                                    borderRadius: 2,
                                }}
                            >
                                {loading ? 'Invio in corso...' : 'Conferma Cancellazione'}
                            </Button>
                        </Box>
                    </Box>
                )}

                {/* STEP 2 — Successo */}
                {activeStep === 2 && (
                    <Box sx={{ p: { xs: 3, md: 4 }, textAlign: 'center' }}>
                        <Box sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: 80,
                            height: 80,
                            borderRadius: '50%',
                            bgcolor: C.greenDim,
                            border: `2px solid ${C.greenBrd}`,
                            mb: 3,
                        }}>
                            <CheckCircleIcon sx={{ fontSize: 44, color: C.green }} />
                        </Box>

                        <Typography variant="h5" sx={{ color: C.white, fontWeight: 800, mb: 1.5 }}>
                            Richiesta Inviata
                        </Typography>
                        <Typography variant="body1" sx={{ color: C.muted, mb: 3 }}>
                            La tua richiesta di cancellazione per l'account{' '}
                            <strong style={{ color: C.yellow }}>{email}</strong>{' '}
                            è stata registrata correttamente.
                        </Typography>

                        <Box sx={{
                            bgcolor: C.dim,
                            border: `1px solid rgba(255,255,255,0.1)`,
                            borderRadius: 2,
                            p: 2,
                            textAlign: 'left',
                            mb: 3,
                        }}>
                            <Typography variant="body2" sx={{ color: C.yellow, fontWeight: 700, mb: 0.5 }}>
                                Cosa succede ora?
                            </Typography>
                            <Typography variant="body2" sx={{ color: C.muted }}>
                                Il nostro team elaborerà la richiesta entro <strong style={{ color: C.white }}>30 giorni</strong>.
                                Riceverai una conferma via email all'indirizzo indicato.
                                Nel frattempo l'account rimane attivo.
                            </Typography>
                        </Box>

                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.25)', display: 'block' }}>
                            Per assistenza:{' '}
                            <strong style={{ color: C.muted }}>info@beathlete.it</strong>
                        </Typography>
                    </Box>
                )}
            </Card>

            {/* ── Footer ── */}
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.18)', mt: 5, textAlign: 'center' }}>
                © {new Date().getFullYear()} beAthlete | P.IVA 02648670905 | info@beathlete.it. Tutti i diritti riservati. —{' '}

            </Typography>
        </Box>
    );
};

export default AccountDeletionPage;
